import React, { useCallback, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme, alpha } from "@mui/material/styles";
import { Header } from "@/components/molecules/Header";
import type { MenuCategory } from "@/types";
import { useInvalidateGuestMenu } from "@/core/menu/guest-hooks";
import { useCart } from "@/hooks/useCart";

import type { ProductListProps } from "./ProductList.types";
import { CategoryNavigation } from "./components/CategoryNavigation/CategoryNavigation";
import { CategorySection } from "./components/CategorySection/CategorySection";
import { FloatingCarts } from "./components/FloatingCarts";
import { useMenu } from "@/hooks";
import { useParentCommunication } from "@/hooks/useParentCommunication";
import { useCategoryScrollContext } from "@/context/CategoryScrollContext";
import { useAppDispatch } from "@/core/store";
import { setSelectedCategory } from "@/core/category";
import { useModalRouter } from "@/hooks/useModalRouter";

/**
 * Main ProductList Component
 */
const ProductListComponent: React.FC<ProductListProps> = ({
  onCategorySelect,
  showCategoryNavigation = true,
  onRefresh,
  className,
  "data-testid": dataTestId,
  ...props
}) => {
  const { sendMessage } = useParentCommunication();
  const { navigateBack, isModalRoute } = useModalRouter();
  const dispatch = useAppDispatch();

  const theme = useTheme();

  // Use global category scroll context
  const {
    activeCategoryId,
    setActiveCategoryId,
    registerScrollContainer,
    registerCategoryRef,
    scrollToCategory,
    setManualNavigation,
  } = useCategoryScrollContext();

  // Local refs for integration with global context
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const manualNavigationRef = useRef(false);

  // Debounce timer refs - moved to component level
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cart state for FloatingCarts component
  const { totalSum, itemCount, isEmpty } = useCart();

  // Меню приезжает гостевым контуром по pointId — грузить его отсюда
  // больше не нужно, useMenu тянет данные сам.
  const { categories, groupedProducts, loading, error, isPartial, products } =
    useMenu();
  const invalidateGuestMenu = useInvalidateGuestMenu();

  // Гейт состояний — по ТОВАРАМ, а не по категориям: словарь категорий —
  // отдельный запрос, и он переживает падение ПЕРВОЙ страницы товаров. Гейт
  // по категориям в этом случае прятал бы и блок ошибки (categories > 0), и
  // плашку (isPartial требует хотя бы одной страницы) — гость видел бы
  // пустые заголовки категорий без единого признака сбоя.
  const hasProducts = products.length > 0;

  // Helper function to get error message
  const getErrorMessage = useCallback((error: unknown): string => {
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    return "Произошла ошибка при загрузке данных";
  }, []);

  // Intersection Observer for tracking visible categories
  useEffect(() => {
    if (!scrollContainerRef.current || loading) {
      return;
    }

    // Simple scroll-based approach for better accuracy
    const handleScroll = () => {
      if (manualNavigationRef.current) {
        return;
      }

      // Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the scroll handler
      debounceTimerRef.current = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const scrollTop = container.scrollTop;

        // Special case: if we're at the very top, select first category
        if (scrollTop <= 50 && categories.length > 0) {
          const firstCategoryId = categories[0].id;
          if (activeCategoryId !== firstCategoryId) {
            setActiveCategoryId(firstCategoryId);
          }
          return;
        }

        // Find the category that is most visible in the top portion of the container
        let bestCategoryId: string | null = null;
        let bestScore = -1;

        // Calculate header height dynamically
        const stickyHeader = container.querySelector(
          '[data-sticky-header="true"]',
        );
        const headerHeight = stickyHeader
          ? stickyHeader.getBoundingClientRect().height
          : 120;
        const topThreshold = headerHeight + 50; // Consider area below header as priority area

        categories.forEach((category) => {
          const element = categoryRefs.current[category.id];
          if (!element) return;

          const rect = element.getBoundingClientRect();

          // Calculate how much of the category is visible in the viewport
          const viewportHeight = window.innerHeight;
          const visibleTop = Math.max(rect.top, 0);
          const visibleBottom = Math.min(rect.bottom, viewportHeight);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > 0) {
            // Score based on visibility and position
            let score = visibleHeight;

            // Bonus for being in the top area
            if (rect.top <= topThreshold) {
              score += 200;
            }

            // Penalty for being far from the top
            const distanceFromTop = Math.max(0, rect.top);
            score -= distanceFromTop * 0.5;

            if (score > bestScore) {
              bestScore = score;
              bestCategoryId = category.id;
            }
          }
        });

        if (bestCategoryId && activeCategoryId !== bestCategoryId) {
          setActiveCategoryId(bestCategoryId);
        }
      }, 150); // 150ms debounce
    };

    const container = scrollContainerRef.current;
    container.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);

      // Clear timers on cleanup
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (scrollDebounceTimerRef.current) {
        clearTimeout(scrollDebounceTimerRef.current);
      }
    };
  }, [categories, activeCategoryId]);

  // Set initial active category
  useEffect(() => {
    if (categories.length > 0 && !activeCategoryId) {
      setActiveCategoryId(categories[0].id);
    }
  }, [categories, activeCategoryId, setActiveCategoryId]);

  // Register scroll container with global context
  useEffect(() => {
    if (scrollContainerRef.current) {
      registerScrollContainer(scrollContainerRef);
    }
  }, [registerScrollContainer]);

  // Register category refs with global context
  useEffect(() => {
    Object.entries(categoryRefs.current).forEach(([categoryId, ref]) => {
      registerCategoryRef(categoryId, ref);
    });
  }, [categories, registerCategoryRef]);

  // Handle category selection using global scroll context
  const handleCategorySelect = useCallback(
    (category: MenuCategory) => {
      // Update Redux state for selected category
      dispatch(setSelectedCategory(category.id));

      // Update local active category ID
      if (activeCategoryId !== category.id) {
        setActiveCategoryId(category.id);
      }

      // Set local manual navigation flag
      manualNavigationRef.current = true;

      // Use global scroll functionality
      scrollToCategory(category.id);
      setManualNavigation(true);

      // Reset local manual navigation flag after scroll completes
      setTimeout(() => {
        manualNavigationRef.current = false;
        setManualNavigation(false);
      }, 1000);

      onCategorySelect?.(category);
    },
    [
      dispatch,
      onCategorySelect,
      scrollToCategory,
      setManualNavigation,
      setActiveCategoryId,
      activeCategoryId,
    ],
  );

  // Кнопка «Попробовать снова» в блоке ошибки: гасим кэш меню, чтобы
  // TanStack перезапросил его. Через useInvalidateGuestMenu, а не по путям:
  // обход товаров живёт под своим ключом, и по пути он не гасится — кнопка
  // обновляла бы категории, оставляя товары в ошибке, то есть выглядела бы
  // сломанной ровно там, где она и нужна.
  const handleRefresh = useCallback(() => {
    invalidateGuestMenu();
    onRefresh?.();
  }, [invalidateGuestMenu, onRefresh]);

  // Handle close modal - use navigateBack if in modal route (with animation),
  // otherwise send REQUEST_CLOSE to parent (SDK)
  const handleCloseModal = useCallback(() => {
    if (isModalRoute()) {
      // We're in a modal route, use navigateBack which triggers animation
      navigateBack();
    } else {
      // We're in iframe, send REQUEST_CLOSE to parent (SDK)
      sendMessage("REQUEST_CLOSE");
    }
  }, [sendMessage, navigateBack, isModalRoute]);

  // Первичной загрузки здесь больше нет: useMenu тянет меню по pointId
  // сам. Оставленный эффект перестреливал бы при появлении brandId и давал
  // вторую полную загрузку на каждом открытии виджета.

  return (
    <Box
      className={className}
      data-testid={dataTestId}
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "100vh",
        maxWidth: "100%",
        overflow: "hidden",
        flex: 1,
      }}
      {...props}
    >
      {/* Main Content */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box
          data-sticky-header="true"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            width: "100%",
            backgroundColor: (theme) =>
              alpha(theme.palette.background.default, 0.85),
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)", // Safari support
          }}
        >
          {/* Header */}
          <Header
            title="ПРЕДЗАКАЗ"
            transparent
            showBackButton={true}
            onBackClick={handleCloseModal}
            showCloseButton={true}
            onCloseClick={handleCloseModal}
          />

          {/* Category Navigation */}
          {showCategoryNavigation && categories.length > 1 && (
            <CategoryNavigation
              categories={categories}
              selectedCategoryId={activeCategoryId || undefined}
              onCategorySelect={handleCategorySelect}
              loading={loading}
            />
          )}
        </Box>
        <Box
          component="main"
          sx={{
            paddingBottom: "80px", // Отступ снизу, чтобы элементы навигации не закрывали карточки товаров
          }}
        >
          {/* Error State — только когда показывать нечего вовсе. Обход меню
              может оборваться на середине (круг 7), и прежнее `error &&
              !loading` прятало бы уже приехавшие блюда. */}
          {error && !loading && !hasProducts && (
            <Box
              sx={{
                textAlign: "center",
                py: theme.spacing(4),
              }}
            >
              <Typography variant="h6" color="error" gutterBottom>
                Ошибка загрузки
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: theme.spacing(2) }}
              >
                {getErrorMessage(error)}
              </Typography>
              <Button variant="outlined" onClick={handleRefresh}>
                Попробовать снова
              </Button>
            </Box>
          )}

          {/* Меню загружено частично: показываем то, что есть, и говорим об
              этом. Кнопка та же, что в блоке ошибки, — перезагрузить меню. */}
          {isPartial && !loading && hasProducts && (
            <Box sx={{ textAlign: "center", py: theme.spacing(2) }}>
              <Typography variant="body2" color="text.secondary">
                Меню загружено не полностью — показана часть блюд.
              </Typography>
              <Button variant="text" onClick={handleRefresh}>
                Обновить
              </Button>
            </Box>
          )}

          {/* Products by Category */}
          {(!error || hasProducts) && (
            <>
              {categories.length === 0 && !loading && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: theme.spacing(8),
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Блюда не найдены
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    В данный момент нет доступных блюд для отображения
                  </Typography>
                </Box>
              )}

              {Object.keys(groupedProducts).map((categoryId) => {
                const products = groupedProducts[categoryId]?.products || [];
                return (
                  <Box
                    key={categoryId}
                    data-category-id={categoryId}
                    ref={(el: HTMLDivElement | null) => {
                      categoryRefs.current[categoryId] = el;
                    }}
                  >
                    <CategorySection
                      category={groupedProducts[categoryId].category}
                      products={products}
                      loading={loading}
                    />
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>

      {/* Floating Cart Button */}
      <FloatingCarts
        itemCount={itemCount}
        totalSum={totalSum}
        isEmpty={isEmpty}
      />
    </Box>
  );
};

// Set display name for debugging
ProductListComponent.displayName = "ProductList";

// Apply memoization with performance monitoring
export const ProductList = ProductListComponent;
